import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { PortalShell } from "@/components/layout/PortalShell";
import { PortalPage } from "@/components/portal/PortalPage";
import { CLIENT_NAV } from "@/lib/client-nav";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useUserRoles } from "@/hooks/useUserRoles";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  Line,
  ComposedChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { FileText } from "lucide-react";

export const Route = createFileRoute("/client/policies/$policyId")({
  head: () => ({
    meta: [{ title: "Policy Detail — Currency Thoughts" }, { name: "robots", content: "noindex" }],
  }),
  component: () => (
    <ProtectedRoute requiredRole="client">
      <PortalShell title="Family Command Center" nav={CLIENT_NAV} tourKey="client_tour">
        <PolicyDetailPage />
      </PortalShell>
    </ProtectedRoute>
  ),
});

interface Policy {
  id: string;
  carrier: string | null;
  product_type: string | null;
  policy_number: string | null;
  face_amount: number | null;
  premium: number | null;
  status: string;
  effective_date: string | null;
  client_id: string;
  agent_id: string | null;
}
interface PolicyValue {
  id: string;
  value_date: string;
  cash_value: number | null;
  account_value: number | null;
  total_premium_paid: number | null;
  death_benefit: number | null;
  loan_balance: number;
  notes: string | null;
  updated_by: string | null;
  created_at: string;
}
interface Doc {
  id: string;
  name: string;
  bucket: string;
  path: string;
  created_at: string;
}

const fmt = (v: number | null | undefined) =>
  v == null
    ? "—"
    : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(v);
const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

function PolicyDetailPage() {
  const { policyId } = Route.useParams();
  const { user } = useAuth();
  const { roles } = useUserRoles();
  const isStaff = roles.some((r) => r === "agent" || r === "admin" || r === "super_admin");

  const [policy, setPolicy] = useState<Policy | null>(null);
  const [values, setValues] = useState<PolicyValue[]>([]);
  const [docs, setDocs] = useState<Doc[]>([]);
  const [advisorName, setAdvisorName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data: p } = await supabase
      .from("policies")
      .select("id, carrier, product_type, policy_number, face_amount, premium, status, effective_date, client_id, agent_id")
      .eq("id", policyId)
      .maybeSingle();
    setPolicy(p as Policy | null);

    const [{ data: vs }, { data: ds }, agent] = await Promise.all([
      supabase
        .from("policy_values")
        .select("*")
        .eq("policy_id", policyId)
        .order("value_date", { ascending: true }),
      supabase
        .from("documents")
        .select("id, name, bucket, path, created_at")
        .eq("client_id", (p as Policy | null)?.client_id ?? "00000000-0000-0000-0000-000000000000")
        .order("created_at", { ascending: false }),
      (p as Policy | null)?.agent_id
        ? supabase
            .from("profiles")
            .select("full_name")
            .eq("id", (p as Policy).agent_id!)
            .maybeSingle()
        : Promise.resolve({ data: null }),
    ]);
    setValues((vs ?? []) as PolicyValue[]);
    setDocs((ds ?? []) as Doc[]);
    setAdvisorName(((agent.data as any)?.full_name as string | null) ?? null);
    setLoading(false);
  };

  useEffect(() => {
    void load();
  }, [policyId]);

  const latest = values[values.length - 1];
  const useAccount = (policy?.product_type ?? "").toLowerCase().includes("annuit");
  const currentValue = useAccount ? latest?.account_value : latest?.cash_value;
  const valueLabel = useAccount ? "Account Value" : "Cash Value";

  const chartData = values.map((v) => ({
    date: fmtDate(v.value_date),
    value: (useAccount ? v.account_value : v.cash_value) ?? 0,
    contributions: v.total_premium_paid ?? 0,
  }));

  if (loading) {
    return (
      <PortalPage eyebrow="Policy" title="Loading…" intro="">
        <p className="font-sans text-charcoal-500">Loading policy…</p>
      </PortalPage>
    );
  }
  if (!policy) {
    return (
      <PortalPage eyebrow="Policy" title="Policy not found" intro="">
        <Link to="/client/policies" className="text-bronze-600 hover:underline font-sans">
          ← Back to policies
        </Link>
      </PortalPage>
    );
  }

  return (
    <PortalPage
      eyebrow={policy.carrier ?? "Policy"}
      title={`${policy.product_type ?? "Policy"} · ${policy.policy_number ?? "—"}`}
      intro={`Effective ${policy.effective_date ? fmtDate(policy.effective_date) : "—"}. A living view of how your policy is performing over time.`}
      actions={
        isStaff ? (
          <RecordValuesDialog policyId={policy.id} userId={user?.id ?? null} onSaved={load} />
        ) : undefined
      }
    >
      <Link to="/client/policies" className="font-sans text-[12px] text-bronze-600 hover:underline">
        ← All policies
      </Link>

      {/* Section 1: Current values */}
      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <ValueCard
          label={`Current ${valueLabel}`}
          value={fmt(currentValue ?? null)}
          highlight
        />
        <ValueCard label="Total Premium Paid" value={fmt(latest?.total_premium_paid ?? null)} />
        <ValueCard label="Death Benefit" value={fmt(latest?.death_benefit ?? policy.face_amount)} />
        <ValueCard
          label="Last Updated"
          value={latest ? fmtDate(latest.value_date) : "—"}
          sub={advisorName ? `by ${advisorName}` : undefined}
        />
      </div>

      {/* Section 2: Chart */}
      <div className="mt-8 bg-white border border-bronze-600/20 rounded-[4px] p-6 shadow-soft">
        <p className="font-sans text-[10px] font-semibold uppercase tracking-[0.22em] text-bronze-600">
          — Growth Over Time —
        </p>
        <h3 className="mt-2 font-display font-semibold text-navy-800 text-[22px]">
          {valueLabel} vs. contributions
        </h3>
        {chartData.length === 0 ? (
          <div className="mt-6 py-10 text-center">
            <p className="font-sans text-charcoal-500">
              No values recorded yet. Your Strategist will post updates after each policy statement.
            </p>
          </div>
        ) : (
          <div className="mt-4" style={{ width: "100%", height: 320 }}>
            <ResponsiveContainer>
              <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="valueFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#A88B5C" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#A88B5C" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#A88B5C" strokeOpacity={0.2} strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fill: "#6B7280", fontSize: 12, fontFamily: "Inter" }} axisLine={false} tickLine={false} />
                <YAxis
                  tick={{ fill: "#6B7280", fontSize: 12, fontFamily: "Inter" }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => (v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`)}
                />
                <Tooltip
                  contentStyle={{ background: "#fff", border: "1px solid #A88B5C", borderRadius: 4, fontFamily: "Inter", fontSize: 12 }}
                  formatter={(v: number, name: string) => [fmt(v), name === "value" ? valueLabel : "Contributions"]}
                />
                <Legend formatter={(name) => (name === "value" ? valueLabel : "Total contributions")} />
                <Area type="monotone" dataKey="value" stroke="#0A3369" strokeWidth={2} fill="url(#valueFill)" />
                <Line type="monotone" dataKey="contributions" stroke="#6B7280" strokeWidth={2} strokeDasharray="4 4" dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Section 3: Projections CTA */}
      <div className="mt-8 bg-cream-50 border border-bronze-600/30 rounded-[4px] p-6">
        <p className="font-sans text-[10px] font-semibold uppercase tracking-[0.22em] text-bronze-600">
          — Projected Values —
        </p>
        <p className="mt-2 font-display text-navy-800 text-[20px]">
          Your Strategist can run a fresh illustration anytime.
        </p>
        <p className="mt-2 font-sans text-[14px] text-charcoal-900">
          Curious what this policy could look like at age 65, 75, or 85?{" "}
          {advisorName ? `Request an updated illustration from ${advisorName}.` : "Request an updated illustration from your Strategist."}
        </p>
        <div className="mt-4">
          <Link to="/client/messages">
            <PrimaryButton>Request illustration</PrimaryButton>
          </Link>
        </div>
      </div>

      {/* Section 4: Documents */}
      <div className="mt-8 bg-white border border-bronze-600/20 rounded-[4px] p-6 shadow-soft">
        <p className="font-sans text-[10px] font-semibold uppercase tracking-[0.22em] text-bronze-600">
          — Documents —
        </p>
        {docs.length === 0 ? (
          <p className="mt-4 font-sans text-charcoal-500">No documents attached yet.</p>
        ) : (
          <ul className="mt-4 divide-y divide-bronze-600/15">
            {docs.map((d) => (
              <li key={d.id} className="py-3 flex items-center gap-3">
                <FileText size={16} className="text-bronze-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-sans text-[13px] text-charcoal-900 truncate">{d.name}</p>
                  <p className="font-sans text-[11px] text-charcoal-500">{fmtDate(d.created_at)}</p>
                </div>
                <button
                  onClick={async () => {
                    const { data } = await supabase.storage
                      .from(d.bucket)
                      .createSignedUrl(d.path, 3600);
                    if (data?.signedUrl) window.open(data.signedUrl, "_blank");
                  }}
                  className="font-sans text-[11px] uppercase tracking-[0.18em] text-bronze-600 hover:text-navy-800"
                >
                  Download
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </PortalPage>
  );
}

function ValueCard({
  label,
  value,
  sub,
  highlight,
}: {
  label: string;
  value: string;
  sub?: string;
  highlight?: boolean;
}) {
  return (
    <div className="bg-white border-t-[3px] border-t-bronze-600 border-x border-b border-bronze-600/20 rounded-[4px] p-5 shadow-soft">
      <p className="font-sans text-[10px] font-semibold uppercase tracking-[0.22em] text-bronze-600">
        {label}
      </p>
      <p
        className={`mt-3 font-display font-semibold leading-none ${
          highlight
            ? "text-[32px] bg-gradient-to-br from-bronze-600 to-bronze-400 bg-clip-text text-transparent"
            : "text-[24px] text-navy-800"
        }`}
      >
        {value}
      </p>
      {sub && <p className="mt-2 font-sans text-[11px] text-charcoal-500">{sub}</p>}
    </div>
  );
}

function RecordValuesDialog({
  policyId,
  userId,
  onSaved,
}: {
  policyId: string;
  userId: string | null;
  onSaved: () => void | Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    value_date: new Date().toISOString().slice(0, 10),
    cash_value: "",
    account_value: "",
    total_premium_paid: "",
    death_benefit: "",
    loan_balance: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  const num = (s: string) => (s === "" ? null : Number(s));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <PrimaryButton>Record Policy Values</PrimaryButton>
      </DialogTrigger>
      <DialogContent className="bg-cream-50 max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-navy-800 text-[22px]">
            Record policy values
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Value date</Label>
            <Input type="date" value={form.value_date} onChange={(e) => setForm({ ...form, value_date: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Cash value ($)</Label>
              <Input type="number" value={form.cash_value} onChange={(e) => setForm({ ...form, cash_value: e.target.value })} />
            </div>
            <div>
              <Label>Account value ($)</Label>
              <Input type="number" value={form.account_value} onChange={(e) => setForm({ ...form, account_value: e.target.value })} />
            </div>
            <div>
              <Label>Premium paid ($)</Label>
              <Input type="number" value={form.total_premium_paid} onChange={(e) => setForm({ ...form, total_premium_paid: e.target.value })} />
            </div>
            <div>
              <Label>Death benefit ($)</Label>
              <Input type="number" value={form.death_benefit} onChange={(e) => setForm({ ...form, death_benefit: e.target.value })} />
            </div>
            <div>
              <Label>Loan balance ($)</Label>
              <Input type="number" value={form.loan_balance} onChange={(e) => setForm({ ...form, loan_balance: e.target.value })} />
            </div>
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
          </div>
          <PrimaryButton
            disabled={saving}
            className="w-full"
            onClick={async () => {
              setSaving(true);
              const { error } = await supabase.from("policy_values").upsert(
                {
                  policy_id: policyId,
                  value_date: form.value_date,
                  cash_value: num(form.cash_value),
                  account_value: num(form.account_value),
                  total_premium_paid: num(form.total_premium_paid),
                  death_benefit: num(form.death_benefit),
                  loan_balance: form.loan_balance === "" ? 0 : Number(form.loan_balance),
                  notes: form.notes || null,
                  updated_by: userId,
                } as any,
                { onConflict: "policy_id,value_date" },
              );
              setSaving(false);
              if (error) {
                toast.error(error.message);
                return;
              }
              if (userId) {
                await supabase.from("activity_logs").insert({
                  user_id: userId,
                  action: "policy.value_updated",
                  target_type: "policy",
                  target_id: policyId,
                  metadata: { value_date: form.value_date },
                });
              }
              toast.success("Policy values saved");
              setOpen(false);
              await onSaved();
            }}
          >
            {saving ? "Saving…" : "Save values"}
          </PrimaryButton>
        </div>
      </DialogContent>
    </Dialog>
  );
}